import api from "../api";

const rentsService = {
  // Get all rents with DataTable support
  getAllRents: async (params = {}, signal = null) => {
    try {
      const config = signal ? { signal } : {};
      const response = await api.post("/rents", {
        draw: params.draw || 1,
        start: params.start || 0,
        length: params.length || 30,
        search: params.search || { value: "" },
        order: params.order || [{ column: 0, dir: "asc" }],
      }, config);
      return response.data;
    } catch (error) {
      console.error("Error fetching rents:", error);
      throw error;
    }
  },

  // Get rent by ID
  getRentById: async (id) => {
    try {
      const response = await api.get(`/rents/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching rent:", error);
      throw error;
    }
  },

  // Create new rent
  createRent: async (rentData) => {
    try {
      const response = await api.post("/rents/create", rentData);
      return response.data;
    } catch (error) {
      console.error("Error creating rent:", error);
      throw error;
    }
  },

  // Update rent
  updateRent: async (id, rentData) => {
    try {
      const response = await api.put(`/rents/${id}`, rentData);
      return response.data;
    } catch (error) {
      console.error("Error updating rent:", error);
      throw error;
    }
  },

  // Delete rent
  deleteRent: async (id) => {
    try {
      const response = await api.delete(`/rents/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting rent:", error);
      throw error;
    }
  },

  // Get rent payments
  getRentPayments: async (rentId) => {
    try {
      // Add skipAuthLogout flag to prevent automatic logout on auth errors
      const response = await api.get(`/rents/${rentId}/payments`, {
        skipAuthLogout: true,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching rent payments:", error);
      // Don't throw - return error object to prevent logout
      return {
        success: false,
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to load rent payments",
        rent: null,
        payments: [],
        summary: null,
      };
    }
  },

  // Get payment edit data
  getPaymentEdit: async (rentId, paymentId) => {
    try {
      const response = await api.get(
        `/rents/${rentId}/payments/${paymentId}/edit`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching payment edit data:", error);
      throw error;
    }
  },

  // Update payment
  updatePayment: async (rentId, paymentId, paymentData) => {
    try {
      const response = await api.put(
        `/rents/${rentId}/payments/${paymentId}`,
        paymentData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating payment:", error);
      throw error;
    }
  },

  // Update rent payments
  updateRentPayments: async (rentId, paymentData) => {
    try {
      const response = await api.post(
        `/rents/${rentId}/payments/edit`,
        paymentData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating rent payments:", error);
      throw error;
    }
  },

  // Get rent dates edit data
  getRentDatesEdit: async (rentId) => {
    try {
      const response = await api.get(`/rents/${rentId}/dates/edit`);
      return response.data;
    } catch (error) {
      console.error("Error fetching rent dates edit data:", error);
      throw error;
    }
  },

  // Update rent dates
  updateRentDates: async (rentId, datesData) => {
    try {
      const response = await api.post(`/rents/${rentId}/dates/edit`, datesData);
      return response.data;
    } catch (error) {
      console.error("Error updating rent dates:", error);
      throw error;
    }
  },

  // Get guardian officer edit data
  getGuardianOfficerEdit: async (rentId) => {
    try {
      const response = await api.get(`/rents/${rentId}/officer/edit`);
      return response.data;
    } catch (error) {
      console.error("Error fetching guardian officer edit data:", error);
      throw error;
    }
  },

  // Update guardian officer
  updateGuardianOfficer: async (rentId, officerData) => {
    try {
      const response = await api.post(
        `/rents/${rentId}/officer/update`,
        officerData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating guardian officer:", error);
      throw error;
    }
  },

  // Export rents to CSV
  exportToCSV: async (params = {}) => {
    try {
      const response = await api.post("/rents/export/csv", params, {
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      console.error("Error exporting rents to CSV:", error);
      throw error;
    }
  },

  // Export rents to PDF
  exportToPDF: async (params = {}) => {
    try {
      const response = await api.post("/rents/export/pdf", params, {
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      console.error("Error exporting rents to PDF:", error);
      throw error;
    }
  },

  // Get rent licences edit data
  getRentLicencesEdit: async (rentId) => {
    try {
      const response = await api.get(`/rents/${rentId}/licences/edit`);
      return response.data;
    } catch (error) {
      console.error("Error fetching rent licences edit data:", error);
      throw error;
    }
  },

  // Get presigned upload URL for license agreement
  getLicenseAgreementUploadUrl: async (rentId, documentType, contentType = 'application/pdf') => {
    try {
      const response = await api.get(
        `/rents/${rentId}/licences/upload-url/${documentType}`,
        {
          params: { contentType }
        }
      );
      
      if (!response.data.success || !response.data.presignedUrl) {
        throw new Error(response.data.message || "Failed to get presigned upload URL");
      }
      
      return {
        presignedUrl: response.data.presignedUrl,
        filename: response.data.filename,
        s3Key: response.data.s3Key,
      };
    } catch (error) {
      console.error("Error getting presigned upload URL:", error);
      throw error;
    }
  },

  // Upload file to S3 using presigned URL
  uploadFileToS3: async (presignedUrl, file) => {
    try {
      console.log(`[S3 UPLOAD] Starting upload: ${file.name} (${file.size} bytes)`);
      
      const response = await fetch(presignedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'application/pdf',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[S3 UPLOAD] Upload failed: ${response.status} ${response.statusText}`, errorText);
        throw new Error(`S3 upload failed: ${response.status} ${response.statusText}. ${errorText}`);
      }

      // Verify upload succeeded (S3 returns 200 OK on successful PUT)
      if (response.status !== 200) {
        throw new Error(`S3 upload returned unexpected status: ${response.status}`);
      }

      console.log(`✅ [S3 UPLOAD] File uploaded to S3 successfully: ${file.name}`);
      return true;
    } catch (error) {
      console.error("[S3 UPLOAD] Error uploading file to S3:", error);
      throw error;
    }
  },

  // Update rent licences (after files are uploaded to S3)
  updateRentLicences: async (rentId, licenceData) => {
    try {
      // Send filenames (files already uploaded to S3 via presigned URLs)
      const response = await api.post(
        `/rents/${rentId}/licences/edit`,
        licenceData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating rent licences:", error);
      throw error;
    }
  },

  // Delete licence document
  deleteLicenceDocument: async (rentId, documentType) => {
    try {
      // Backend route: DELETE /api/rents/:rentId/license-destroy/:fieldName
      const response = await api.delete(
        `/rents/${rentId}/license-destroy/${documentType}`
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting licence document:", error);
      throw error;
    }
  },

  // Generate licence agreement (returns PDF blob for download)
  generateLicenceAgreement: async (rentId) => {
    try {
      const response = await api.get(
        `/rents/${rentId}/licences/generate/license_agreement`,
        {
          responseType: 'blob', // Important: expect binary data, not JSON
        }
      );
      
      // Check if response is actually an error JSON (sometimes errors come as blobs)
      const contentType = response.headers['content-type'] || '';
      if (contentType.includes('application/json')) {
        // Error response came as JSON blob, parse it
        const errorText = await response.data.text();
        const errorData = JSON.parse(errorText);
        throw new Error(errorData.message || 'Failed to generate license agreement');
      }
      
      // Verify it's a PDF
      if (!contentType.includes('application/pdf')) {
        console.warn('Unexpected content type:', contentType);
      }
      
      // response.data is already a Blob when responseType is 'blob'
      const blob = response.data;
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `license_agreement_${rentId}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);
      
      console.log(`✅ License agreement downloaded: ${filename}`);
      return { success: true, filename };
    } catch (error) {
      console.error("Error generating licence agreement:", error);
      
      // If error response is a blob, try to parse it as JSON
      if (error.response?.data instanceof Blob) {
        try {
          const errorText = await error.response.data.text();
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.message || 'Failed to generate license agreement');
        } catch (parseError) {
          // If parsing fails, use original error
          throw error;
        }
      }
      
      throw error;
    }
  },

  // Get licence document with presigned URL (direct S3 download)
  getLicenceDocument: async (rentId, documentType) => {
    try {
      // Backend returns JSON with presigned URL (not blob)
      const response = await api.get(
        `/rents/${rentId}/licences/${documentType}/view`
      );
      
      if (!response.data.success || !response.data.downloadUrl) {
        throw new Error(response.data.message || "Failed to get presigned URL");
      }
      
      // Return presigned URL for direct S3 download (matching Laravel's direct S3 access)
      const presignedUrl = response.data.downloadUrl;
      console.log(`✅ Using presigned URL for direct S3 download: ${response.data.filename}`);
      
      return presignedUrl;
    } catch (error) {
      console.error("Error getting licence document:", error);
      throw error;
    }
  },

  // Get licence document from revision with presigned URL (direct S3 download)
  getLicenceDocumentFromRevision: async (rentId, documentType, revisionId) => {
    try {
      const endpoint = `/rents/${rentId}/licences/${documentType}/revision/${revisionId}/view`;
      console.log(`[RENTS SERVICE] Fetching revision file (NOT current file):`, {
        endpoint,
        rentId,
        documentType,
        revisionId,
        note: 'This endpoint fetches from agreements_history table, not rents table'
      });
      
      // Backend returns JSON with presigned URL (not blob)
      const response = await api.get(endpoint);
      
      if (!response.data.success || !response.data.downloadUrl) {
        throw new Error(response.data.message || "Failed to get presigned URL");
      }
      
      // Return presigned URL for direct S3 download (matching Laravel's direct S3 access)
      const presignedUrl = response.data.downloadUrl;
      console.log(`✅ [RENTS SERVICE] Using presigned URL for revision file:`, {
        filename: response.data.filename,
        revisionId,
        note: 'This is the revision file from agreements_history'
      });
      
      return presignedUrl;
    } catch (error) {
      console.error("[RENTS SERVICE] Error getting licence document from revision:", {
        error: error.message,
        response: error.response?.data,
        rentId,
        documentType,
        revisionId
      });
      throw error;
    }
  },

  // ===== Notice to Quit Functions =====

  // Get all notice to quit records (list page)
  getNoticeToQuitList: async () => {
    try {
      const response = await api.get("/rents/notice-to-quit");
      return response.data;
    } catch (error) {
      console.error("Error fetching notice to quit list:", error);
      throw error;
    }
  },

  // Get notice to quit create data
  getNoticeToQuitCreate: async (rentId) => {
    try {
      const response = await api.get(`/rents/${rentId}/notice-to-quit/create`);
      return response.data;
    } catch (error) {
      console.error("Error fetching notice to quit create data:", error);
      throw error;
    }
  },

  // Create notice to quit
  createNoticeToQuit: async (rentId, ntqData) => {
    try {
      const response = await api.post(
        `/rents/${rentId}/notice-to-quit/create`,
        ntqData
      );
      return response.data;
    } catch (error) {
      console.error("Error creating notice to quit:", error);
      throw error;
    }
  },

  // Get notice to quit edit data
  getNoticeToQuitEdit: async (rentId, ntqId) => {
    try {
      const response = await api.get(
        `/rents/${rentId}/notice-to-quit/${ntqId}/edit`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching notice to quit edit data:", error);
      throw error;
    }
  },

  // Update notice to quit
  updateNoticeToQuit: async (rentId, ntqId, ntqData) => {
    try {
      const response = await api.put(
        `/rents/${rentId}/notice-to-quit/edit`,
        ntqData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating notice to quit:", error);
      throw error;
    }
  },

  // Delete notice to quit
  deleteNoticeToQuit: async (rentId, ntqId) => {
    try {
      const response = await api.delete(
        `/rents/${rentId}/notice-to-quit/destroy`
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting notice to quit:", error);
      throw error;
    }
  },

  // Upload notice to quit file
  uploadNoticeToQuitFile: async (rentId, file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await api.post(
        `/rents/${rentId}/notice-to-quit/storeFile`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error uploading notice to quit file:", error);
      throw error;
    }
  },

  // Download notice to quit with presigned URL
  downloadNoticeToQuit: async (rentId) => {
    try {
      console.log(`[NTQ Download] Getting presigned URL for rentId: ${rentId}`);
      
      // Get presigned URL from backend
      const response = await api.get(
        `/rents/${rentId}/notice-to-quit/presigned-url`
      );

      if (!response.data || !response.data.success || !response.data.presignedUrl) {
        throw new Error(response.data?.message || "No presigned URL returned");
      }

      const { presignedUrl, filename } = response.data;
      const downloadFilename = filename || `notice-to-quit-${rentId}.pdf`;
      
      console.log(`[NTQ Download] Presigned URL received, fetching file...`);

      // Fetch file from S3 presigned URL
      const fileResponse = await fetch(presignedUrl, {
        method: "GET",
        mode: 'cors',
      });

      if (!fileResponse.ok) {
        throw new Error(`Failed to download file from S3: ${fileResponse.status}`);
      }

      // Get file as blob
      const blob = await fileResponse.blob();
      console.log(`[NTQ Download] Blob received: ${blob.size} bytes`);
      
      if (blob.size === 0) {
        throw new Error("Downloaded file is empty (0 bytes)");
      }

      // Create blob URL (same-origin, so download attribute will work)
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = downloadFilename;
      link.style.display = 'none';
      
      // Add to DOM, trigger click, then remove
      document.body.appendChild(link);
      console.log(`[NTQ Download] Triggering download for: ${downloadFilename}`);
      link.click();
      
      // Clean up after download starts
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        console.log(`[NTQ Download] Cleanup complete`);
      }, 200);

      return { success: true, message: "File downloaded successfully" };
    } catch (error) {
      console.error("Error downloading notice to quit:", error);
      throw error;
    }
  },

  // Delete notice to quit file
  deleteNoticeToQuitFile: async (rentId) => {
    try {
      const response = await api.delete(
        `/rents/${rentId}/notice-to-quit/delete`
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting notice to quit file:", error);
      throw error;
    }
  },

  // ===== Rent Status Operations =====

  // Finish rent
  finishRent: async (rentId) => {
    try {
      const response = await api.get(`/rents/${rentId}/finish`);
      return response.data;
    } catch (error) {
      console.error("Error finishing rent:", error);
      throw error;
    }
  },

  // Fail rent
  failRent: async (rentId) => {
    try {
      const response = await api.get(`/rents/${rentId}/fail`);
      return response.data;
    } catch (error) {
      console.error("Error failing rent:", error);
      throw error;
    }
  },

  // Reactivate rent
  reactivateRent: async (rentId) => {
    try {
      const response = await api.get(`/rents/${rentId}/reactivate`);
      return response.data;
    } catch (error) {
      console.error("Error reactivating rent:", error);
      throw error;
    }
  },

  // Restore rent
  restoreRent: async (rentId) => {
    try {
      const response = await api.get(`/rents/${rentId}/restore`);
      return response.data;
    } catch (error) {
      console.error("Error restoring rent:", error);
      throw error;
    }
  },

  // Get start rent data (for Start Rent page)
  getStartRentData: async (rentId) => {
    try {
      const response = await api.get(`/rents/${rentId}/start`);
      return response.data;
    } catch (error) {
      console.error("Error fetching start rent data:", error);
      throw error;
    }
  },

  // Start rent (POST request)
  startRent: async (rentId, rentData) => {
    try {
      const response = await api.post(`/rents/${rentId}/start`, rentData);
      return response.data;
    } catch (error) {
      console.error("Error starting rent:", error);
      throw error;
    }
  },
};

export default rentsService;
